import { useMemo, useState } from 'react'

import { useHistory } from 'react-router-dom'

import {
  authActions,
  useIsSessionActive,
  useAuthIsLoggingIn,
} from 'core/auth/authStore'
import { useBetaPages } from 'core/beta'
import {
  Clickable,
  DropdownMenu,
  StyledDropdownMenuItem,
  StyledDropdownMenuSeparator,
  StyledNonClickableDropdownMenuItem,
} from 'core/components'
import Icon from 'core/components/Icon'
import {
  useActiveEnv,
  useCustomEnvs,
  setActiveId,
  STANDARD_ENVIRONMENTS,
  selectStandardEnvironment,
  Env,
} from 'core/stores/env'
import { useDarkModeInfo } from 'core/theme/DarkModeProvider'

import AppUserInfo from '../AppUserInfo'

import AddEnvDialog from './AddEnvDialog'
import { Box, StyledSelect, SubLabel } from './common'
import RemoveEnvDialog from './RemoveEnvDialog'

type EnvOptionType = {
  value: string
  label: string
}

type GroupedEnvOptionType = {
  label: string
  options: Array<EnvOptionType>
}

const MainMenu = () => {
  const { darkMode, setDarkMode } = useDarkModeInfo()
  const { betaPages, setBetaPages } = useBetaPages()
  const history = useHistory()

  const customEnvs = useCustomEnvs()
  const activeEnv = useActiveEnv()

  const isSessionActive = useIsSessionActive()
  const isLoggingIn = useAuthIsLoggingIn()

  const [isAddEnvDialogOpen, setIsAddEnvDialogOpen] = useState(false)
  const [isRemoveEnvDialogOpen, setIsRemoveEnvDialogOpen] = useState(false)

  const environmentSelectOptions = useMemo((): Array<GroupedEnvOptionType> => {
    const options: Array<GroupedEnvOptionType> = []

    if (STANDARD_ENVIRONMENTS.length > 0) {
      options.push({
        label: 'Standard Environments',
        options: STANDARD_ENVIRONMENTS.map((stdEnv) => ({
          value: `standard_${stdEnv.id}`,
          label: stdEnv.label,
          sublabel: (
            <SubLabel>
              <span>{stdEnv.apiBase}</span>
              <span>{stdEnv.companyId}</span>
            </SubLabel>
          ),
        })),
      })
    }

    if (customEnvs.length > 0) {
      options.push({
        label: 'Custom Environments',
        options: customEnvs.map((customEnv: Env) => ({
          value: `custom_${customEnv.id}`,
          label: customEnv.label || 'Unnamed Custom Environment',
          sublabel: (
            <SubLabel>
              <span>{customEnv.apiBase}</span>
              <span>{customEnv.companyId}</span>
            </SubLabel>
          ),
        })),
      })
    }
    return options
  }, [customEnvs])

  const currentSelectValue = useMemo(() => {
    if (!activeEnv) return null
    const prefix = activeEnv.isStandard ? 'standard_' : 'custom_'
    return `${prefix}${activeEnv.id}`
  }, [activeEnv])

  const handleEnvironmentChange = async (
    selectedOptionValue: string | null,
  ) => {
    if (!selectedOptionValue) return

    const [type, ...idParts] = selectedOptionValue.split('_')
    const id = idParts.join('_')

    authActions.setInitializingAsTrue() // Should make App.tsx show spinner

    // Give React a chance to re-render with the spinner
    await new Promise((resolve) => setTimeout(resolve, 100)) // Short delay

    if (type === 'standard') {
      selectStandardEnvironment(id)
    } else if (type === 'custom') {
      setActiveId(id)
    }

    // Give React a chance to re-render if env change causes it
    await new Promise((resolve) => setTimeout(resolve, 100))

    await authActions.initializeSessionAndUser(id)

    history.push('/home')
  }

  const envLabel = activeEnv?.label || 'Environment'

  const envMenu = (
    <DropdownMenu
      trigger={
        <Clickable variant='outline'>
          <Icon name='settings' />
          <Icon fontSize='24px' name='arrow_drop_down' />
        </Clickable>
      }
    >
      <StyledNonClickableDropdownMenuItem>
        <AppUserInfo />
      </StyledNonClickableDropdownMenuItem>
      <StyledDropdownMenuSeparator />
      <StyledDropdownMenuItem
        onSelect={() => {
          setTimeout(() => setIsAddEnvDialogOpen(true), 50)
        }}
      >
        Add Custom Environment
      </StyledDropdownMenuItem>
      <StyledDropdownMenuItem
        disabled={customEnvs.length === 0}
        onSelect={() => {
          setTimeout(() => setIsRemoveEnvDialogOpen(true), 50)
        }}
      >
        Remove Custom Environment
      </StyledDropdownMenuItem>
      <StyledDropdownMenuSeparator />
      {!isSessionActive ? (
        <StyledDropdownMenuItem
          disabled={isLoggingIn}
          onSelect={() => authActions.setLoginModalExplicitlyRequested(true)}
        >
          Login
        </StyledDropdownMenuItem>
      ) : (
        <StyledDropdownMenuItem
          disabled={isLoggingIn}
          onSelect={() => {
            authActions.logout()
            window.location.reload()
          }}
        >
          {`Logout of ${envLabel}`}
        </StyledDropdownMenuItem>
      )}
      <StyledDropdownMenuSeparator />
      <StyledDropdownMenuItem
        onSelect={() => {
          setDarkMode(darkMode === 'dark' ? 'light' : 'dark')
        }}
      >
        {darkMode === 'dark' ? '🌞 Light Mode' : '🌚 Dark Mode'}
      </StyledDropdownMenuItem>
      <StyledDropdownMenuItem onSelect={() => setBetaPages(!betaPages)}>
        {betaPages ? '🪄 Turn Beta Pages Off' : '🪄 Turn Beta Pages On'}
      </StyledDropdownMenuItem>
    </DropdownMenu>
  )

  return (
    <>
      <Box>
        <StyledSelect
          isSearchable={false}
          onChange={handleEnvironmentChange}
          options={environmentSelectOptions}
          placeholder='Select Environment...'
          value={currentSelectValue}
        />
        {envMenu}
      </Box>
      <AddEnvDialog
        isOpen={isAddEnvDialogOpen}
        onOpenChange={setIsAddEnvDialogOpen}
      />
      <RemoveEnvDialog
        isOpen={isRemoveEnvDialogOpen}
        onOpenChange={setIsRemoveEnvDialogOpen}
      />
    </>
  )
}

export default MainMenu
